import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Send, Loader2, Trash2, Sparkles, Mic, MicOff, Volume2, VolumeX, Square } from 'lucide-react';
import api from '../services/api';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const synth = window.speechSynthesis;

export default function ChatPanel() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text || loading) return;
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    try {
      const response = await api.post('/ai/chat', { messages: newMessages });
      const reply = response.data.reply;
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      speak(reply);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Blad polaczenia z AI';
      setMessages(prev => [...prev, { role: 'assistant', content: `**Blad:** ${errorMsg}` }]);
    } finally {
      setLoading(false);
    }
  };

  const stripMarkdown = (text) => text.replace(/\*\*(.+?)\*\*/g, '$1').replace(/`([^`]+)`/g, '$1').replace(/^[*-] /gm, '').trim();

  const speak = (text) => {
    if (!synth || !ttsEnabled) return;
    synth.cancel();
    const clean = stripMarkdown(text);
    if (!clean) return;
    const utterance = new SpeechSynthesisUtterance(clean);
    utterance.lang = 'pl-PL';
    utterance.rate = 1.05;
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    synth.speak(utterance);
  };

  const stopSpeaking = () => { if (synth) synth.cancel(); setSpeaking(false); };

  const startVoice = () => {
    if (!SpeechRecognition || loading) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'pl-PL'; recognition.interimResults = false; recognition.continuous = true;
    recognitionRef.current = recognition;
    recognition.onresult = (e) => {
      const text = Array.from(e.results).map(r => r[0].transcript).join(' ').trim();
      if (text) sendMessage(text);
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognition.start(); setListening(true);
  };

  const stopVoice = () => { recognitionRef.current?.stop(); };

  const renderContent = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      let rendered = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      rendered = rendered.replace(/`([^`]+)`/g, '<code class="bg-gray-200 px-1 rounded text-xs">$1</code>');
      if (rendered.startsWith('- ') || rendered.startsWith('* ')) rendered = '&bull; ' + rendered.substring(2);
      return <span key={i}><span dangerouslySetInnerHTML={{ __html: rendered }} />{i < text.split('\n').length - 1 && <br />}</span>;
    });
  };

  return (
    <>
      {!isOpen && (
        <button onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 z-50 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all hover:scale-105"
          title={t('chat.assistantTitle')}>
          <Sparkles size={24} />
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-4 right-4 z-50 w-96 max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-6rem)] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-blue-600 text-white rounded-t-xl">
            <div className="flex items-center gap-2">
              <Sparkles size={18} />
              <span className="font-medium text-sm">{t('chat.assistantTitle')}</span>
            </div>
            <div className="flex items-center gap-1">
              {synth && (
                <button onClick={() => { if (ttsEnabled) stopSpeaking(); setTtsEnabled(!ttsEnabled); }}
                  className={`p-1.5 rounded transition ${ttsEnabled ? 'bg-blue-500' : 'hover:bg-blue-700'} ${speaking ? 'animate-pulse' : ''}`}
                  title={ttsEnabled ? t('chat.disableTts') : t('chat.enableTts')}>
                  {ttsEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                </button>
              )}
              {messages.length > 0 && (
                <button onClick={() => { stopSpeaking(); setMessages([]); }} className="p-1.5 hover:bg-blue-700 rounded transition" title={t('chat.clearChat')}>
                  <Trash2 size={16} />
                </button>
              )}
              <button onClick={() => { stopSpeaking(); setIsOpen(false); }} className="p-1.5 hover:bg-blue-700 rounded transition" title={t('common.close')}>
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-gray-400 text-sm mt-8">
                <Sparkles size={32} className="mx-auto mb-2 text-blue-300" />
                <p className="font-medium text-gray-500">{t('chat.assistantTitle')}</p>
                <p className="mt-1">{t('chat.welcomeMessage')}</p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm leading-relaxed ${
                  msg.role === 'user' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                }`}>
                  {renderContent(msg.content)}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-500 px-3 py-2 rounded-lg rounded-bl-sm text-sm flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> {t('chat.thinking')}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {speaking && (
            <div className="px-3 py-2 border-t border-blue-200 bg-blue-50 flex items-center justify-between">
              <span className="text-xs text-blue-600 animate-pulse">{t('chat.readingResponse')}</span>
              <button onClick={stopSpeaking} className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs">
                <Square size={12} fill="currentColor" /> Stop
              </button>
            </div>
          )}

          {/* Input */}
          <div className="px-3 py-3 border-t border-gray-200">
            <div className="flex gap-1.5">
              {SpeechRecognition && (
                <button onMouseDown={startVoice} onMouseUp={stopVoice} onMouseLeave={listening ? stopVoice : undefined}
                  disabled={loading}
                  className={`px-2.5 py-2 rounded-lg transition select-none ${listening ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-blue-600'}`}
                  title={t('chat.holdToSpeak')}>
                  {listening ? <MicOff size={16} /> : <Mic size={16} />}
                </button>
              )}
              <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input.trim()); } }}
                placeholder={listening ? t('chat.listening') : t('chat.writePlaceholder')} disabled={loading}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <button onClick={() => sendMessage(input.trim())} disabled={loading || !input.trim()}
                className="px-2.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg transition" title={t('chat.send')}>
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
